import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Center,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  convertMagnifiedToDecimal,
  STOCK_UI,
} from '@diamond/shared/decimal-util-web';
import {
  BackofficeActivitiesOrderItem,
  BackofficeActivitiesPromos,
} from '@diamond/shared/types';
import { DataTable, LabelPromo } from '@diamond/shared/ui';
import {
  ProductRewardImages,
  PromoReward,
  VoucherReward,
} from '@diamond/sol-buyer/utils';
import { CellContext, createColumnHelper, Row } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useProductRewards } from '../../hooks/use-product-rewards';
import {
  ActivityImageObject,
  formatCurrency,
  getActivityItemDisplay,
  getItemCode,
  getOrderItemDisplaySubtotal,
  resolveActivityCheckoutDisplaySubtotal,
} from '../../utils/activity-formatters';
import { ActivityDetailDiffLabel } from '../activity-detail/activity-detail-stage-qty-cell';
import {
  CancelReasonColumn,
  ConfirmedQuantityColumn,
  DeliveredQuantityColumn,
  ImageColumn,
  IsNotProcessedColumn,
  IsUpdatedColumn,
  LatestPriceColumn,
  NoteColumn,
  PriceColumn,
  ProcessedQuantityColumn,
  ReceivedQuantityColumn,
  StorageTypeColumn,
  ValuationTypeColumn,
} from '../activity-details-columns';
import { CheckIconVoucher } from './Icons';

// Base type for items displayed in Promo & Voucher section
// Supports both voucher free goods (from order_items) and promo free goods (from order.promos)
export type PromoVoucherFreeGoodItem = {
  id: string;
  product_id: string;
  title?: string | null;
  quantity?: number;
  quantity_free_goods?: number | null;
  price?: number;
  subtotal?: number;
  item_code?: number;
  images?: ActivityImageObject;
  product_images?: string[];
  product_image_thumbnails?: string[];
  product_images_medium?: string[];
  product_images_large?: string[];
  source: 'promo' | 'voucher';
  voucher_code?: string;
  reward_type?: string;
  // Optional fields from order_items (voucher)
  processed_quantity?: number | null;
  delivered_quantity?: number | null;
  received_quantity?: number | null;
  confirmed_quantity?: number | null;
  latest_price?: number | null;
  note?: string | null;
  is_not_processed?: boolean;
  is_updated?: boolean;
  product_storage?: string | null;
  valuation_type?: string | null;
  cancel_reason_id?: string | null;
  // Promo-specific fields
  mechanism?: string | null;
  promo_code?: string;
  free_goods_product_item_code?: number | null;
};

const columnHelper = createColumnHelper<PromoVoucherFreeGoodItem>();

const toBackofficeInfo = <T,>(
  info: CellContext<PromoVoucherFreeGoodItem, unknown>
) => info as unknown as CellContext<BackofficeActivitiesOrderItem, T>;

const readonlySelectionProps = {
  selectedData: [] as Row<BackofficeActivitiesOrderItem>[],
  onUpdate: () => undefined,
  activitiesId: '',
  token: undefined,
  refetch: () => undefined,
};

const isEmptyValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'number') {
    return value === 0;
  }

  if (typeof value === 'string') {
    return value.length === 0;
  }

  return false;
};

type ConditionalDisplayConfig<TValue> = {
  id: string;
  header: string;
  accessor: (row: PromoVoucherFreeGoodItem) => TValue | null | undefined;
  render: (
    info: CellContext<BackofficeActivitiesOrderItem, TValue>
  ) => React.ReactNode;
  isEmpty?: (value: TValue | null | undefined) => boolean;
};

const createConditionalDisplayColumn = <TValue,>({
  id,
  header,
  accessor,
  render,
  isEmpty,
}: ConditionalDisplayConfig<TValue>) =>
  columnHelper.accessor(accessor, {
    id,
    header,
    cell: (info) => {
      const value = info.getValue();
      const checkEmpty = isEmpty ?? ((val) => isEmptyValue(val));
      if (checkEmpty(value)) {
        return (
          <Text fontSize="sm" fontWeight="400" color="#373F41">
            -
          </Text>
        );
      }
      return render(toBackofficeInfo<TValue>(info));
    },
  });

/**
 * Get LabelPromo type based on reward_type from voucher
 */
const getLabelPromoType = (
  rewardType?: string
): 'free-goods' | 'discount' | 'cashback' => {
  if (!rewardType) return 'free-goods';

  switch (rewardType) {
    case 'DISCOUNT_PERCENT':
    case 'DISCOUNT_AMOUNT':
      return 'discount';
    case 'CASHBACK':
      return 'cashback';
    case 'FREE_GOODS':
    default:
      return 'free-goods';
  }
};

/**
 * Convert ProductRewardImages from API to ActivityImageObject format
 */
const convertToActivityImageObject = (
  images: ProductRewardImages
): ActivityImageObject => ({
  image: images.image,
  thumbnail_url: images.thumbnail_url ?? undefined,
  medium_image_url: images.medium_image_url ?? undefined,
  large_image_url: images.large_image_url ?? undefined,
});

const findOrderPromoFreeGood = (
  orderPromos: BackofficeActivitiesPromos | null | undefined,
  promoId: string
) => {
  if (!orderPromos) {
    return undefined;
  }

  return [
    ...(orderPromos.single_free_good_promos ?? []),
    ...(orderPromos.bundled_free_good_promos ?? []),
  ].find((promo) => promo.id === promoId);
};

const toProductRewardImages = (
  orderPromo: NonNullable<ReturnType<typeof findOrderPromoFreeGood>>,
  fallback: ProductRewardImages
): ProductRewardImages => {
  if (!orderPromo.product_images?.[0]) {
    return fallback;
  }

  return {
    image: orderPromo.product_images[0],
    thumbnail_url: orderPromo.product_image_thumbnails?.[0] ?? null,
    medium_image_url: orderPromo.product_images_medium?.[0] ?? null,
    large_image_url: orderPromo.product_images_large?.[0] ?? null,
  };
};

/**
 * Common reward item properties shared between promo and voucher
 */
type BaseRewardInput = {
  id: string;
  product_name: string;
  quantity: number;
  product_item_code: string;
  images: ProductRewardImages;
  free_goods_product_item_code?: number | null;
};

const resolveRewardItemCode = (reward: BaseRewardInput): number | undefined => {
  const itemCode = getItemCode({
    quantity_free_goods: reward.quantity,
    free_goods_product_item_code: reward.free_goods_product_item_code,
    product_item_code: reward.product_item_code,
    item_code: Number(reward.product_item_code) || undefined,
  });

  return itemCode ? Number(itemCode) : undefined;
};

/**
 * Maps a reward (promo or voucher) to PromoVoucherFreeGoodItem format
 * Extracts common mapping logic to reduce code duplication
 */
const mapRewardToItem = <T extends BaseRewardInput>(
  reward: T,
  source: 'promo' | 'voucher',
  extraFields: Partial<PromoVoucherFreeGoodItem> = {}
): PromoVoucherFreeGoodItem => ({
  id: reward.id,
  product_id: reward.id,
  title: reward.product_name,
  quantity: reward.quantity,
  quantity_free_goods: convertMagnifiedToDecimal(reward.quantity, STOCK_UI),
  price: 0,
  subtotal: 0,
  item_code: resolveRewardItemCode(reward),
  free_goods_product_item_code: reward.free_goods_product_item_code,
  images: convertToActivityImageObject(reward.images),
  source,
  reward_type: 'FREE_GOODS',
  ...extraFields,
});

const columns = [
  columnHelper.accessor('source', {
    cell: (info) => {
      const isVoucher = info.getValue() === 'voucher';
      const rewardType = info.row.original.reward_type;
      const labelType = getLabelPromoType(rewardType);

      return (
        <VStack alignItems="flex-start" gap="1">
          <HStack gap="2">
            {isVoucher && <CheckIconVoucher boxSize="20px" color="#34C759" />}
            <LabelPromo type={labelType} />
          </HStack>
          <Text fontSize="sm" fontWeight="400" color="#373F41">
            {isVoucher ? 'Pakai Voucher' : 'Promo'}
          </Text>
        </VStack>
      );
    },
    header: '',
  }),
  columnHelper.accessor((row) => row.item_code, {
    id: 'item-code',
    header: 'Kode Item',
    cell: (info) => {
      const value = info.getValue();
      return (
        <Text fontSize="sm" fontWeight="400" color="#373F41">
          {value ?? '-'}
        </Text>
      );
    },
  }),
  columnHelper.display({
    id: 'image',
    header: 'Gambar',
    cell: (info) => (
      <ImageColumn
        info={toBackofficeInfo<ActivityImageObject | string[] | undefined>(
          info
        )}
      />
    ),
  }),
  columnHelper.accessor((row) => row.title ?? '-', {
    id: 'product-name',
    header: 'Nama Produk',
    cell: (info) => {
      const name = info.row.original.title ?? info.getValue() ?? '-';
      return (
        <Text fontSize="sm" fontWeight="400" color="#373F41">
          {name}
        </Text>
      );
    },
  }),
  columnHelper.accessor((row) => row.quantity, {
    id: 'checkout-quantity',
    header: 'Qty. Checkout',
    cell: (info) => {
      const value = info.getValue();
      if (isEmptyValue(value)) {
        return (
          <Text fontSize="sm" fontWeight="400" color="#373F41">
            -
          </Text>
        );
      }

      return (
        <Text fontSize="sm" fontWeight="400" color="#373F41">
          {convertMagnifiedToDecimal(
            info.row.original.quantity ?? value ?? 0,
            STOCK_UI
          )}
        </Text>
      );
    },
  }),
  createConditionalDisplayColumn<number | undefined>({
    id: 'confirmed-quantity',
    header: 'Qty. Dikonfirmasi',
    accessor: (row) => row.confirmed_quantity,
    render: (info) => {
      const display = getActivityItemDisplay(info.row.original);

      return (
        <VStack align="flex-start" spacing={0}>
          <ConfirmedQuantityColumn info={info} {...readonlySelectionProps} />
          {display.showInitialQuantity ? (
            <ActivityDetailDiffLabel
              label="Pesanan Awal"
              value={String(display.checkoutQuantity)}
              align="left"
            />
          ) : null}
        </VStack>
      );
    },
  }),
  columnHelper.accessor((row) => row.price, {
    id: 'price',
    header: 'Harga Checkout',
    cell: (info) => {
      const isFreeGoods =
        info.row.original.quantity_free_goods &&
        Number(info.row.original.quantity_free_goods) > 0;
      const value = info.getValue();
      if (!isFreeGoods && isEmptyValue(value)) {
        return (
          <Text fontSize="sm" fontWeight="400" color="#373F41">
            -
          </Text>
        );
      }
      return isFreeGoods ? (
        <HStack alignItems="center" gap="2">
          <Text fontSize="sm" fontWeight="400" color="#373F41">
            {formatCurrency(0)}
          </Text>
          <Box
            px="2"
            py="0.5"
            bg="#FFDADA"
            borderRadius="4px"
            color="#FF0000"
            fontSize="xs"
            fontWeight="semibold"
            lineHeight="16px"
          >
            Gratis
          </Box>
        </HStack>
      ) : (
        <PriceColumn info={toBackofficeInfo<number>(info)} isVoucherEnabled />
      );
    },
  }),
  createConditionalDisplayColumn<number | undefined>({
    id: 'latest-price',
    header: 'Harga Akhir',
    accessor: (row) => row.latest_price,
    render: (info) => (
      <LatestPriceColumn
        info={info}
        isVoucherEnabled
        {...readonlySelectionProps}
      />
    ),
  }),
  columnHelper.accessor((row) => row.subtotal, {
    id: 'subtotal',
    header: 'Total Harga',
    cell: (info) => {
      const item = info.row.original;
      const display = getActivityItemDisplay(item);
      const lineSubtotal = getOrderItemDisplaySubtotal(item);

      if (display.showInitialTotalPrice) {
        return (
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="sm" fontWeight="400" color="#373F41">
              {formatCurrency(lineSubtotal)}
            </Text>
            <ActivityDetailDiffLabel
              label="total awal"
              value={formatCurrency(display.initialTotalPrice)}
              align="left"
            />
          </VStack>
        );
      }

      const checkoutSubtotal = resolveActivityCheckoutDisplaySubtotal(item);

      if (checkoutSubtotal == null) {
        return (
          <Text fontSize="sm" fontWeight="400" color="#373F41">
            -
          </Text>
        );
      }

      return (
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="sm" fontWeight="400" color="#373F41">
            {formatCurrency(checkoutSubtotal)}
          </Text>
        </VStack>
      );
    },
  }),
  columnHelper.accessor((row) => row.note ?? null, {
    id: 'note',
    header: 'Catatan Produk',
    cell: (info) => (
      <NoteColumn
        info={toBackofficeInfo<string | null | undefined>(info)}
        {...readonlySelectionProps}
      />
    ),
  }),
  columnHelper.accessor((row) => row.is_not_processed ?? false, {
    id: 'is-not-processed',
    header: 'Tidak Diproses',
    cell: (info) => (
      <Box pointerEvents="none">
        <IsNotProcessedColumn
          info={toBackofficeInfo<boolean | undefined>(info)}
          {...readonlySelectionProps}
        />
      </Box>
    ),
  }),
  columnHelper.accessor((row) => row.is_updated, {
    id: 'is-updated',
    header: 'Pernah Diubah',
    cell: (info) => (
      <IsUpdatedColumn info={toBackofficeInfo<boolean | undefined>(info)} />
    ),
  }),
  columnHelper.accessor((row) => row.product_storage ?? null, {
    id: 'storage-type',
    header: 'Tempat Penyimpanan',
    cell: (info) => (
      <StorageTypeColumn
        info={toBackofficeInfo<string | null | undefined>(info)}
        {...readonlySelectionProps}
      />
    ),
  }),
  columnHelper.accessor((row) => row.valuation_type ?? null, {
    id: 'valuation-type',
    header: 'Valuation Type',
    cell: (info) => (
      <ValuationTypeColumn
        info={toBackofficeInfo<string | null | undefined>(info)}
        {...readonlySelectionProps}
      />
    ),
  }),
  columnHelper.accessor((row) => row.cancel_reason_id ?? null, {
    id: 'cancel-reason',
    header: 'Alasan Pembatalan',
    cell: (info) => (
      <CancelReasonColumn
        info={toBackofficeInfo<string | null | undefined>(info)}
        {...readonlySelectionProps}
        cancelReasonData={[]}
      />
    ),
  }),
  createConditionalDisplayColumn<number | undefined>({
    id: 'processed-quantity',
    header: 'Diproses',
    accessor: (row) => row.processed_quantity,
    render: (info) => <ProcessedQuantityColumn info={info} />,
  }),
  createConditionalDisplayColumn<number | undefined>({
    id: 'delivered-quantity',
    header: 'Dikirim',
    accessor: (row) => row.delivered_quantity,
    render: (info) => <DeliveredQuantityColumn info={info} />,
  }),
  createConditionalDisplayColumn<number | undefined>({
    id: 'received-quantity',
    header: 'Diterima',
    accessor: (row) => row.received_quantity,
    render: (info) => <ReceivedQuantityColumn info={info} />,
  }),
];

interface ActivityDetailPromoVoucherSectionProps {
  orderId: string;
  orderPromos?: BackofficeActivitiesPromos | null;
}

export const ActivityDetailPromoVoucherSection = ({
  orderId,
  orderPromos,
}: ActivityDetailPromoVoucherSectionProps) => {
  const { data, isLoading, isError } = useProductRewards(orderId);

  const items = useMemo((): PromoVoucherFreeGoodItem[] => {
    if (!data) return [];

    const promoItems = (data.promo_rewards || []).map((promo: PromoReward) => {
      const orderPromo = findOrderPromoFreeGood(orderPromos, promo.promo_id);
      const rewardItemCode =
        orderPromo?.free_goods_product_item_code ??
        Number(promo.product_item_code);

      return mapRewardToItem(
        {
          ...promo,
          id: promo.promo_id,
          product_item_code: String(rewardItemCode),
          product_name: orderPromo?.title ?? promo.product_name,
          free_goods_product_item_code:
            orderPromo?.free_goods_product_item_code,
          images: orderPromo
            ? toProductRewardImages(orderPromo, promo.images)
            : promo.images,
        },
        'promo',
        { mechanism: promo.mechanism }
      );
    });

    const voucherItems = (data.voucher_rewards || []).map(
      (voucher: VoucherReward) =>
        mapRewardToItem({ ...voucher, id: voucher.voucher_id }, 'voucher', {
          voucher_code: voucher.voucher_code,
        })
    );

    return [...promoItems, ...voucherItems];
  }, [data, orderPromos]);

  if (isLoading) {
    return (
      <Box my={8} width="full">
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          Promo & Voucher
        </Text>
        <Center py={8}>
          <Spinner size="lg" />
        </Center>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box my={8} width="full">
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          Promo & Voucher
        </Text>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Gagal memuat data promo & voucher. Silakan coba lagi.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Box my={8} width="full">
      <Text fontSize="xl" fontWeight="bold" mb={4}>
        Promo & Voucher
      </Text>
      <DataTable columns={columns} data={items} />
    </Box>
  );
};
